import { Link } from 'react-router';
import { formatEuro } from '@brimo/shared';
import { clsx } from '../../../ds/clsx';
import { Button } from '../../../ds/components';
import { useIsMobile } from '../../../ds/hooks';
import type { ListRow, ListResponse } from '../types';
import { StagePill } from '../ui/StagePill';
import styles from './list.module.css';

export interface FinancingsListProps {
  view: ListResponse['view'];
  rows: ListRow[];
  query: string;
  dimmed: boolean;
  hasMore: boolean;
  loadingMore: boolean;
  onLoadMore: () => void;
}

function amountOf(row: ListRow): string {
  return row.amountCents === null ? '—' : formatEuro(row.amountCents);
}

/**
 * The table card of Finanzierungen 01/02 (seven columns, every row a link to the record) and the card list
 * below 768 px; „Weitere laden" follows the cursor of the API (page size 50).
 */
export function FinancingsList({
  view,
  rows,
  query,
  dimmed,
  hasMore,
  loadingMore,
  onLoadMore,
}: FinancingsListProps) {
  const mobile = useIsMobile();
  const lastHead = view === 'financier' ? 'Nächster Schritt' : 'Finanzierer · nächster Schritt';

  if (rows.length === 0) {
    return (
      <div className={styles.card}>
        <div className={styles.noHits}>
          {query ? `Keine Finanzierung zu „${query}“.` : 'Keine Finanzierung in diesem Filter.'}
        </div>
      </div>
    );
  }

  const more = hasMore ? (
    <div className={styles.more}>
      <Button variant="secondary" size="small" loading={loadingMore} onClick={onLoadMore}>
        Weitere laden
      </Button>
    </div>
  ) : null;

  if (mobile) {
    return (
      <div className={clsx(styles.cards, dimmed && styles.dimmed)}>
        {rows.map((row) => (
          <Link key={row.id} to={`/financing/${row.id}`} className={styles.mobileCard}>
            <span className={styles.mobileTop}>
              <span className={styles.number}>{row.numberLabel}</span>
              <StagePill stage={row.stage} />
            </span>
            <span className={styles.contact}>{row.contact.name}</span>
            <span className={clsx(styles.deal, row.dealLine.startsWith('ohne Deal') && styles.muted)}>
              {row.dealLine}
            </span>
            <span className={styles.mobileBottom}>
              <span className={styles.amount}>{amountOf(row)}</span>
              <span className={styles.next}>{row.nextStep}</span>
            </span>
          </Link>
        ))}
        {more}
      </div>
    );
  }

  return (
    <div className={clsx(styles.card, dimmed && styles.dimmed)}>
      <div className={clsx(styles.grid, styles.head)} aria-hidden="true">
        <span>Nr.</span>
        <span>Kontakt</span>
        <span>Deal · Einheit</span>
        <span>Art</span>
        <span className={styles.right}>Betrag</span>
        <span>Stufe</span>
        <span>{lastHead}</span>
      </div>
      <ul className={styles.rows} aria-label="Finanzierungen">
        {rows.map((row) => (
          <li key={row.id}>
            <Link
              to={`/financing/${row.id}`}
              className={clsx(styles.grid, styles.row)}
              aria-label={`${row.numberLabel} · ${row.contact.name} · ${row.stage.label}`}
            >
              <span className={styles.number}>{row.numberLabel}</span>
              <span className={styles.contact}>{row.contact.name}</span>
              <span className={clsx(styles.deal, row.dealLine.startsWith('ohne Deal') && styles.muted)}>
                {row.dealLine}
              </span>
              <span className={styles.type}>{row.typeLabel}</span>
              <span className={styles.amount}>{amountOf(row)}</span>
              <span className={styles.stage}>
                <StagePill stage={row.stage} />
              </span>
              <span className={styles.next}>{row.nextStep}</span>
            </Link>
          </li>
        ))}
      </ul>
      {more}
    </div>
  );
}
